import * as THREE from 'three';
import { ARENA_RADIUS, type VisionSource } from '@moba/shared';

// la visión vive en shared: el servidor la usa para enviar a cada equipo sólo lo que ve
export { isVisible, visionSources, type VisionSource } from '@moba/shared';

/** Número máximo de focos de visión que el shader puede procesar. */
const MAX_SOURCES = 48;

/**
 * Niebla de guerra en el suelo: un plano oscuro sobre la arena al que el shader
 * le abre agujeros suaves alrededor de cada foco de visión aliado.
 */
export class FogOfWar {
  readonly mesh: THREE.Mesh;

  private readonly vectors = Array.from({ length: MAX_SOURCES }, () => new THREE.Vector3());
  private readonly uniforms = {
    uVision: { value: this.vectors },
    uCount: { value: 0 },
    uOpacity: { value: 0.84 },
  };

  constructor(scene: THREE.Scene) {
    const size = ARENA_RADIUS * 2 + 40;
    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      vertexShader: /* glsl */ `
        varying vec2 vWorld;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = world.xz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: /* glsl */ `
        #define MAX_SOURCES ${MAX_SOURCES}
        uniform vec3 uVision[MAX_SOURCES];
        uniform int uCount;
        uniform float uOpacity;
        varying vec2 vWorld;

        void main() {
          // fuera de la arena sólo hay decorado: sin niebla (el mar de nubes, el foso...)
          if (length(vWorld) > ${(ARENA_RADIUS + 2).toFixed(1)}) discard;
          float visibility = 0.0;
          for (int i = 0; i < MAX_SOURCES; i++) {
            if (i >= uCount) break;
            vec3 source = uVision[i];
            float dist = distance(vWorld, source.xy);
            visibility = max(visibility, 1.0 - smoothstep(source.z - 1.4, source.z, dist));
          }
          float alpha = uOpacity * (1.0 - visibility);
          if (alpha < 0.004) discard;
          gl_FragColor = vec4(0.012, 0.035, 0.07, alpha);
        }
      `,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = 0.04;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  update(sources: readonly VisionSource[]): void {
    const count = Math.min(sources.length, MAX_SOURCES);
    for (let i = 0; i < count; i++) {
      this.vectors[i].set(sources[i].x, sources[i].z, sources[i].radius);
    }
    this.uniforms.uCount.value = count;
    this.mesh.visible = count > 0;
  }
}
